import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  FileText, 
  Zap,
  AlertTriangle,
  RefreshCw
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface ProgressCheckpoint {
  id: string;
  label: string;
  description: string;
  estimatedSeconds: number;
}

export interface AsyncProgressProps {
  // Core status
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'retry';
  
  // Workflow configuration
  workflowTitle: string;
  workflowDescription: string;
  
  // Progress tracking
  checkpoints: ProgressCheckpoint[];
  currentCheckpointId?: string;
  overallProgress?: number; // 0-100, overrides checkpoint-based calculation
  
  // Timing
  startTime?: Date;
  
  // Documents context
  documents?: Array<{
    fileName: string;
    documentType: string;
  }>;
  
  // Event handlers
  onRefresh?: () => void;
  onCancel?: () => void;
  
  // UI state
  isLoading?: boolean;
  error?: string;
  showDocuments?: boolean;
  showAIIndicator?: boolean;
  className?: string;
}

export default function AsyncProgress({
  status,
  workflowTitle,
  workflowDescription,
  checkpoints,
  currentCheckpointId,
  overallProgress,
  startTime,
  documents = [],
  onRefresh,
  onCancel,
  isLoading = false,
  error,
  showDocuments = true,
  showAIIndicator = true,
  className
}: AsyncProgressProps) {
  const [elapsedTime, setElapsedTime] = React.useState(0);
  const [displayedProgress, setDisplayedProgress] = React.useState(0);
  const [estimatedTotal, setEstimatedTotal] = React.useState(0);

  // Generate progress waypoints based on checkpoint estimates
  const progressWaypoints = React.useMemo(() => {
    const totalTime = checkpoints.reduce((sum, cp) => sum + cp.estimatedSeconds, 0);
    const waypoints: Record<string, number> = {
      pending: 0,
      failed: 0,
      completed: 100
    };
    
    let cumulativeTime = 0;
    checkpoints.forEach(checkpoint => {
      cumulativeTime += checkpoint.estimatedSeconds;
      // Use 95% of progress bar, save 5% for completion
      waypoints[checkpoint.id] = (cumulativeTime / totalTime) * 95;
    });
    
    return waypoints;
  }, [checkpoints]);

  // Calculate target progress from current checkpoint
  const targetProgress = React.useMemo(() => {
    if (overallProgress !== undefined) {
      return overallProgress;
    }
    return progressWaypoints[currentCheckpointId || 'pending'] || 0;
  }, [overallProgress, currentCheckpointId, progressWaypoints]);

  // Track elapsed time for display
  React.useEffect(() => {
    if (status === 'processing' && startTime) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
        setElapsedTime(elapsed);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [status, startTime]);

  // Calculate total estimated time
  React.useEffect(() => {
    const total = checkpoints.reduce((sum, checkpoint) => sum + checkpoint.estimatedSeconds, 0);
    setEstimatedTotal(total);
  }, [checkpoints]);

  // Smooth progress interpolation - simple and reliable
  React.useEffect(() => {
    // For non-processing states, set progress immediately
    if (status !== 'processing' && status !== 'retry') {
      if (status === 'completed') {
        setDisplayedProgress(100);
      } else {
        setDisplayedProgress(0);
      }
      return;
    }

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // For users who prefer reduced motion, update progress immediately
      setDisplayedProgress(targetProgress);
      return;
    }

    // Smooth interpolation to target progress
    const interval = setInterval(() => {
      setDisplayedProgress(prev => {
        const diff = targetProgress - prev;
        
        // Don't animate backwards (except for reset to 0)
        if (diff < 0 && targetProgress > 0) {
          return prev;
        }
        
        // If difference is tiny, snap to target
        if (Math.abs(diff) < 0.1) {
          return targetProgress;
        }
        
        // Smooth interpolation - move 5% of the way toward target
        return prev + (diff * 0.05);
      });
    }, 100);
    
    return () => clearInterval(interval);
  }, [targetProgress, status]);

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
      case 'retry':
        return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-orange-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = () => {
    const badgeClass = cn(
      "capitalize",
      status === 'processing' && "bg-blue-100 text-blue-700 border-blue-200",
      status === 'completed' && "bg-green-100 text-green-700 border-green-200",
      status === 'failed' && "bg-red-100 text-red-700 border-red-200",
      status === 'pending' && "bg-orange-100 text-orange-700 border-orange-200",
      status === 'retry' && "bg-yellow-100 text-yellow-700 border-yellow-200"
    );

    return (
      <Badge variant="outline" className={badgeClass}>
        {status}
      </Badge>
    );
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getCurrentCheckpoint = () => {
    return checkpoints.find(cp => cp.id === currentCheckpointId);
  };

  const displayProgress = displayedProgress;
  const currentCheckpoint = getCurrentCheckpoint();

  // Handle error state
  if (error) {
    return (
      <Card className={cn("border-l-4 border-l-red-500 bg-red-50/30", className)}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2 text-base">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span>Error loading status</span>
            </CardTitle>
            {onRefresh && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onRefresh}
                disabled={isLoading}
              >
                <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
                Retry
              </Button>
            )}
          </div>
          <CardDescription className="text-sm text-red-600">
            {error}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={cn(
      "border-l-4",
      status === 'processing' && "border-l-blue-500 bg-blue-50/30",
      status === 'completed' && "border-l-green-500 bg-green-50/30", 
      status === 'failed' && "border-l-red-500 bg-red-50/30",
      status === 'pending' && "border-l-orange-500 bg-orange-50/30",
      status === 'retry' && "border-l-yellow-500 bg-yellow-50/30",
      className
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2 text-base">
            {getStatusIcon()}
            <span>{workflowTitle}</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            {getStatusBadge()}
            {onRefresh && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onRefresh}
                disabled={isLoading}
              >
                <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
              </Button>
            )}
          </div>
        </div>
        <CardDescription className="text-sm">
          {workflowDescription}
          {documents.length > 0 && ` (${documents.length} document${documents.length === 1 ? '' : 's'})`}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Progress bar for processing status */}
        {(status === 'processing' || status === 'retry') && (
          <div className="space-y-3">
            <div className="relative">
              <Progress 
                value={displayProgress} 
                className={cn(
                  "h-2 transition-all duration-300 ease-out",
                  // Add subtle animation for active processing (respects prefers-reduced-motion)
                  (currentCheckpointId === 'llm_processing' || currentCheckpointId === 'llm_review') && 
                  "motion-safe:animate-pulse"
                )} 
              />
            </div>
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Elapsed: {formatTime(elapsedTime)}</span>
              <span>Est. total: {formatTime(estimatedTotal)}</span>
            </div>
            
            {/* Current checkpoint status */}
            {(currentCheckpoint || currentCheckpointId) && (
              <div className="text-sm text-muted-foreground transition-all duration-200">
                {currentCheckpoint ? (
                  <><span className="font-medium">{currentCheckpoint.label}:</span> {currentCheckpoint.description}</>
                ) : (
                  <><span className="font-medium">Processing:</span> Preparing for next stage</>
                )}
              </div>
            )}
          </div>
        )}

        {/* Document list */}
        {showDocuments && documents.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-sm font-medium text-muted-foreground">
              Documents ({documents.length}):
            </div>
            <div className="space-y-1">
              {documents.map((doc, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm">
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{doc.fileName}</span>
                  <Badge variant="secondary" className="text-xs">
                    {doc.documentType}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI processing indicator */}
        {showAIIndicator && (status === 'processing' || status === 'retry') && (
          <div className="mt-4 flex items-center space-x-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
            <Zap className="h-4 w-4 animate-pulse" />
            <span className="font-medium">AI agent processing</span>
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        )}

        {/* Error details for failed status */}
        {status === 'failed' && (
          <div className="mt-4 flex items-start space-x-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium">Processing failed</div>
              <div className="text-red-500 text-xs mt-1">
                The AI agent was unable to complete processing. This could be due to document format issues or temporary service unavailability.
              </div>
              {onCancel && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Cancel option for processing */}
        {(status === 'processing' || status === 'retry') && onCancel && (
          <div className="mt-4 text-center">
            <Button 
              variant="outline" 
              size="sm"
              onClick={onCancel}
            >
              Cancel Processing
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}